import _ from 'lodash';
import type { UnknownAction } from '@reduxjs/toolkit'

import { 
  FETCH_EXTERNAL_MEDIA_LIBRARY,
  FETCH_MEDIA_LIBRARY, 
  FETCHING_EXTERNAL_MEDIA_LIBRARY_FAILED, 
  FETCHING_EXTERNAL_MEDIA_LIBRARY_SUCCEEDED, 
  FETCHING_MEDIA_LIBRARY_CATEGORIES_SUCCEEDED, 
  FETCHING_MEDIA_LIBRARY_FAILED,
  FETCHING_MEDIA_LIBRARY_SUCCEEDED, 
} from '../actions/actionTypes'

import { 
  FetchExternalMediaLibraryErrorResponse,
  FetchExternalMediaLibraryRequest,
  FetchExternalMediaLibraryResponse,
  FetchMediaLibraryCategoriesResponse,
  FetchMediaLibraryErrorResponse, 
  FetchMediaLibraryRequest, 
  FetchMediaLibraryResponse, 
  MediaLibrary, 
  MediaLibraryCategory
} from '../../types/mediaLibrary';

interface MediaLibraryItemsState {
  loading: boolean;
  mediaType?: 'video' | 'image';
  category?: string | null;
  categories?: MediaLibraryCategory[];
  items: MediaLibrary[];
  error: any,
  pagination: {
    currentPage: number,
    totalPages: number,
    recordsPerPage: number,
    totalRecordsCount: number,
    hasMoreItems: boolean
  },
}

interface ExternalMediaLibraryItemsState {
  loading: boolean;
  mediaType?: 'video' | 'image';
  query?: string | null;
  items: MediaLibrary[];
  error: any,
  pagination: {
    currentPage: number,
    totalPages: number,
    recordsPerPage: number,
    totalRecordsCount: number,
    hasMoreItems: boolean
  },
}

interface MediaLibraryState {
  [key: string]: MediaLibraryItemsState | ExternalMediaLibraryItemsState;
}

const initialState: MediaLibraryState = {

}

const initialItemsState: MediaLibraryItemsState = {
  loading: false,
  items: [],
  categories: [],
  error: null,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false
  }
}

const initialExternalItemsState: ExternalMediaLibraryItemsState = {
  loading: false,
  items: [],
  query: "",
  error: null,
  pagination: {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 10,
    totalRecordsCount: 0,
    hasMoreItems: false
  }
}

export function mediaLibraryReducer(state: MediaLibraryState = initialState, action: UnknownAction) : MediaLibraryState {
  switch (action.type) {
    case FETCH_MEDIA_LIBRARY: {
      const { entityType, mediaType, category } = action.payload as FetchMediaLibraryRequest;

      let itemState = (state[entityType] || initialItemsState) as MediaLibraryItemsState;
      const currentMediaType = itemState.mediaType;
      const currentCategory = itemState.category;

      if(currentMediaType !== mediaType || currentCategory !== category) {
        itemState = {
          ...initialItemsState,
          mediaType: mediaType,
          category: category,
          categories: itemState.categories
        }
      }

      return {
        ...state,
        [entityType]: {
          ...itemState,
          loading: true,
          mediaType: mediaType,
          category: category
        }
      }
    }

    case FETCHING_MEDIA_LIBRARY_SUCCEEDED: {
      const { entityType, category, mediaType, response } = action.payload as FetchMediaLibraryResponse;
      const itemState = state[entityType] as MediaLibraryItemsState;
      if(!itemState) {
        return state;
      }

      let existingItems = itemState.items || [];
      const currentMediaType = itemState.mediaType;
      const currentCategory = itemState.category;

      if(response.currentPage === 0 || currentMediaType !== mediaType || currentCategory !== category) {
        existingItems = response.records;
      }else {
        existingItems = [...existingItems, ...response.records];
      }

      existingItems = _.uniqBy(existingItems, 'id');

      return {
        ...state,
        [entityType]: {
          ...itemState,
          loading: false,
          mediaType: mediaType,
          category: category,
          items: existingItems,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1
          }
        }
      }
    }

    case FETCHING_MEDIA_LIBRARY_FAILED: {
      const { entityType, category, mediaType, error } = action.payload as FetchMediaLibraryErrorResponse;
      const itemState = state[entityType];
      if(!itemState) {
        return state;
      }

      return {
        ...state,
        [entityType]: {
          ...itemState,
          loading: false,
          category: category,
          mediaType: mediaType,
          error: error
        }
      }
    }

    case FETCHING_MEDIA_LIBRARY_CATEGORIES_SUCCEEDED: {
      const { entityType, response } = action.payload as FetchMediaLibraryCategoriesResponse;
      
      const itemState = state[entityType];
      if(!itemState) {
        return state;
      }

      return {
        ...state,
        [entityType]: {
          ...itemState,
          categories: response
        }
      }
    }

    //External Categories
    case FETCH_EXTERNAL_MEDIA_LIBRARY: {
      const { mediaType, mediaLibraryType, query } = action.payload as FetchExternalMediaLibraryRequest;

      let itemState = (state[mediaLibraryType] || initialExternalItemsState) as ExternalMediaLibraryItemsState;
      const currentMediaType = itemState.mediaType;
      const currentQuery = itemState.query;

      if(currentMediaType !== mediaType || currentQuery !== query) {
        itemState = {
          ...initialItemsState,
          mediaType: mediaType,
          query: query
        }
      }

      return {
        ...state,
        [mediaLibraryType]: {
          ...itemState,
          loading: true,
          mediaType: mediaType,
          query: query
        }
      }
    }

    case FETCHING_EXTERNAL_MEDIA_LIBRARY_SUCCEEDED: {
      const { mediaType, mediaLibraryType, query, response } = action.payload as FetchExternalMediaLibraryResponse;
      const itemState = state[mediaLibraryType] as ExternalMediaLibraryItemsState;
      if(!itemState) {
        return state;
      }

      let existingItems = itemState.items || [];
      const currentMediaType = itemState.mediaType;
      const currentQuery = itemState.query;

      if(response.currentPage === 0 || currentMediaType !== mediaType || currentQuery !== query) {
        existingItems = response.records;
      }else {
        existingItems = [...existingItems, ...response.records];
      }

      existingItems = existingItems.map((item) => {
        return {
          ...item,
          id: item.imageUrl
        }
      })

      existingItems = _.uniqBy(existingItems, 'id');

      return {
        ...state,
        [mediaLibraryType]: {
          ...itemState,
          loading: false,
          mediaType: mediaType,
          query: query,
          items: existingItems,
          pagination: {
            currentPage: response.currentPage,
            totalPages: response.totalPages,
            recordsPerPage: response.recordsPerPage,
            totalRecordsCount: response.totalRecordsCount,
            hasMoreItems: response.currentPage < response.totalPages - 1
          }
        }
      }
    }

    case FETCHING_EXTERNAL_MEDIA_LIBRARY_FAILED: {
      const { mediaType, mediaLibraryType, query, error } = action.payload as FetchExternalMediaLibraryErrorResponse;
      const itemState = state[mediaLibraryType];
      if(!itemState) {
        return state;
      }

      return {
        ...state,
        [mediaLibraryType]: {
          ...itemState,
          loading: false,
          query: query,
          mediaType: mediaType,
          error: error
        }
      }
    }

    default: {
      return state
    }
  }
}